// Feature 009: Deal Operating System
// Document list component showing processing status
import React from 'react';

const STATUS_COLORS = {
  uploaded: 'bg-gray-100 text-gray-800',
  processing: 'bg-yellow-100 text-yellow-800',
  extracted: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  uploaded: 'Pending',
  processing: 'Processing',
  extracted: 'Complete',
  failed: 'Failed',
};

const DOCUMENT_TYPE_ICONS = {
  deck: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  email: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  transcript: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  contract: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  default: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
};

const DocumentList = ({ documents, onRetry, csrfToken }) => {
  if (!documents || documents.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Documents</h3>
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="font-medium text-gray-700 mb-1">No documents yet</p>
          <p className="text-sm text-gray-500 mb-4">Upload contracts, decks, or emails to extract key terms.</p>
        </div>
      </div>
    );
  }

  const handleRetry = async (documentId) => {
    if (onRetry) {
      onRetry(documentId);
    }
  };

  const getIcon = (docType) => DOCUMENT_TYPE_ICONS[docType] || DOCUMENT_TYPE_ICONS.default;

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          Documents
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({documents.length})
          </span>
        </h3>
      </div>
      <ul className="divide-y divide-gray-200">
        {documents.map((doc) => (
          <li key={doc.id} className="px-4 py-4 hover:bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0 flex-1">
                <span className="text-gray-400 flex-shrink-0">
                  {getIcon(doc.document_type)}
                </span>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {doc.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 capitalize">
                      {doc.document_type?.replace('_', ' ')}
                    </span>
                    {doc.file_size_bytes && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span className="text-xs text-gray-500">
                          {formatFileSize(doc.file_size_bytes)}
                        </span>
                      </>
                    )}
                    <span className="text-gray-300">•</span>
                    <span className="text-xs text-gray-500">
                      {formatDate(doc.created_at)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                {/* Status Badge */}
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    STATUS_COLORS[doc.processing_status] || STATUS_COLORS.uploaded
                  }`}
                >
                  {doc.processing_status === 'processing' && (
                    <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {STATUS_LABELS[doc.processing_status] || doc.processing_status}
                </span>

                {/* Retry Button for Failed */}
                {doc.processing_status === 'failed' && (
                  <button
                    type="button"
                    onClick={() => handleRetry(doc.id)}
                    className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                  >
                    Retry
                  </button>
                )}

                {/* View/Download Link */}
                {doc.file_url && (
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600"
                    title="View document"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            </div>

            {/* Error Message for Failed */}
            {doc.processing_status === 'failed' && doc.processing_error && (
              <div className="mt-2 ml-8 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-600">
                {doc.processing_error}
              </div>
            )}

            {/* Extracted Fields Preview */}
            {doc.processing_status === 'extracted' && doc.extracted_data && Object.keys(doc.extracted_data).length > 0 && (
              <div className="mt-2 ml-8 p-2 bg-green-50 border border-green-100 rounded">
                <p className="text-xs text-green-700 font-medium mb-1">Extracted Fields:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.keys(doc.extracted_data).slice(0, 5).map((field) => (
                    <span
                      key={field}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800"
                    >
                      {field.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {Object.keys(doc.extracted_data).length > 5 && (
                    <span className="text-xs text-green-600">
                      +{Object.keys(doc.extracted_data).length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DocumentList;
